import { useStandingOfferAccept } from "@/api";
import { toast } from "@/components/ui/use-toast";
import { useNavigate } from "@tanstack/react-router";
import { FormEvent, useState } from "react";

export function useHandleStandingOfferAccept(standingOfferId: string) {
  const [loading, setLoading] = useState(false);
  const [standingOfferAccept] = useStandingOfferAccept();
  const navigate = useNavigate();

  function errorToast() {
    toast({
      title: "Unable to accept offer",
      description:
        "An error occurred while accepting the offer. Please try again or report the issue.",
      variant: "destructive"
    });
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const organizationId =
      (formData.get("organization") as string) || undefined;

    if (!organizationId) return;

    standingOfferAccept({
      variables: { organizationId, standingOfferId }
    })
      .then((res) => {
        if (res.errors) {
          console.error("standing offer accept errors:", res.errors);
          errorToast();
          return;
        }

        switch (res.data?.standingOfferAccept.__typename) {
          case "AppSubscription": {
            toast({
              title: "Offer accepted",
              description: `You can now use the app ${res.data.standingOfferAccept.app.displayName}.`
            });
            navigate({
              to: "/organization/$organizationSlug/gallery",
              params: {
                organizationSlug:
                  res.data.standingOfferAccept.inboundOrganization.slug
              },
              search: {
                filterName: res.data.standingOfferAccept.app.displayName
              }
            });
            break;
          }
          case "SubscriptionOfferCheckoutSession": {
            window.location.replace(res.data.standingOfferAccept.portalURL);
            break;
          }
          case "OrganizationMissingPaymentMethod": {
            toast({
              title: "Missing payment method",
              description:
                "Please add a payment method to your organization to accept the offer.",
              variant: "destructive"
            });
            break;
          }
          case "SubscriptionOfferInvalidStatus": {
            toast({
              title: "Invalid offer",
              variant: "destructive",
              description:
                "The offer is no longer valid as it has been withdrawn."
            });
            break;
          }
          case "SubscriptionOfferNotFound": {
            toast({
              title: "Offer not found",
              variant: "destructive",
              description: "The offer does not exist. Please contact support."
            });
            break;
          }
          case "SubscriptionAlreadyExists": {
            toast({
              title: "Subscription already exists",
              description:
                "The selected organization already has an active subscription."
            });
            break;
          }
          default:
            errorToast();
        }
      })
      .catch((error) => {
        console.error("Offer accept error:", error);

        if ("message" in error && error.message === "access denied") {
          toast({
            title: "Unable to accept offer",
            description: "Only organization admins can accept paid offers.",
            variant: "destructive"
          });
        } else errorToast();
      })
      .finally(() => setLoading(false));
  }

  return { handleSubmit, loading };
}
